import { Base64Url, FileGenerator } from '@adaptiva/core/common';
import { LoggingService } from '@adaptiva/core/logging';
import { BeforeSubmitFn, OverlayService } from '@adaptiva/core/overlays';
import { SnackbarService } from '@adaptiva/core/snackbars';
import { UiService } from '@adaptiva/core/ui';
import { EJavaClassNames, ICloudRelayServerErrors, ObjectManagerService, ObjectSecurityManagerService } from '@adaptiva/data-service';
import { ICloudRelayServer } from '@adaptiva/operations-manager/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, throwError, TimeoutError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { CloudRelayOverlay } from '../cloud-relay-overlay';

@Component({
	standalone: false,
	selector: 'ada-cloud-relay-server-action-overlay',
	templateUrl: './cloud-relay-server-action-overlay.component.html',
	styles: [':host { display: block; height: 100%; }'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CloudRelayServerActionOverlayComponent extends CloudRelayOverlay implements OnInit {

	public errors: ICloudRelayServerErrors = {
		name: new BehaviorSubject<string>(null),
		serverGuid: new BehaviorSubject<string>(null),
		fqdn: new BehaviorSubject<string>(null),
		geolocation: new BehaviorSubject<string>(null)
	};

	public hasErrors: boolean = false;

	constructor(
		protected objectManager: ObjectManagerService,
		protected securityManager: ObjectSecurityManagerService,
		protected snackbarService: SnackbarService,
		_overlayService: OverlayService,
		_uiService: UiService,
		_logger: LoggingService,
	) {
		super(_overlayService, _uiService, _logger, {
			title: 'Create Cloud Relay Server'
		});
	}

	public override ngOnInit(): void {
		super.ngOnInit();

		combineLatest([
			this.errors.name,
			this.errors.serverGuid,
			this.errors.fqdn,
			this.errors.geolocation
		]).pipe(
			this.takeUntilDestroyed()
		).subscribe((_errors: string[]) => {
			this.hasErrors = _errors.some(this.isError.bind(this));
		});
	}

	// submit event for OverlayComponent
	public beforeSubmit: BeforeSubmitFn = (): boolean | Observable<boolean> => {
		if (!this.validate())
			return false;

		return this.objectManager.saveObject<ICloudRelayServer>(
			EJavaClassNames.CloudRelayServer,
			this.context
		).pipe(
			switchMap((_server: ICloudRelayServer) => {
				this.result = _server;

				if (_server)
					return this.securityManager.readCertificateFile(_server.objectId);

				return throwError(() => new Error('Error creating cloud relay server'));
			}),
			map((_cert: string) => {
				if (!_cert) {
					this.logger.error(
						'CloudRelayServerActionOverlayComponent.createNewCloudRelayServer:',
						`Got falsey base64Str value of : ${_cert}`
					)();

					return false;
				}

				return FileGenerator.generate({
					content: [new TextEncoder().encode(atob(_cert)).buffer],
					extension: 'bin',
					fileType: 'application/octet',
					name: `${this.context.name}_certificate`
				});
			}),
			tap(() => {
				this.snackbarService.display('Successfully created cloud relay server');
			}),
			catchError((e: TimeoutError | HttpErrorResponse) => {
				const _message: string = this.logger.extractErrorMessage(e);

				this.overlayService.error('Encountered Errors', _message);
				this.logger.error('CloudRelayServerActionOverlayComponent.beforeSubmit', _message)();

				return of(false);
			})
		);
	};

	public updateName(_name: string): void {
		this.context.name = _name?.trim();
	}

	protected applyContext(_context: ICloudRelayServer): void {
		super.applyContext(_context);
		this.title = _context?.name || 'Create Cloud Relay Server';
	}

	protected isError(_message: string | null): boolean {
		return _message?.length > 0 || false;
	}

	protected override validate(): boolean {
		if (!this.context.name)
			this.errors.name.next('Name Required');
		else
			this.errors.name.next('');

		if (!this.context.serverGuid)
			this.errors.serverGuid.next('Server GUID Required');
		else
			this.errors.serverGuid.next('');

		if (!this.context.fqdn)
			this.errors.fqdn.next('FQDN / IP Address Required');
		else
			this.errors.fqdn.next('');

		this.errors.geolocation.next(this.getGeolocationError());

		return !this.hasErrors;
	}

}
